import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { Monitor, Smartphone, Mouse, Keyboard, Headphones, Printer, Send, CircleCheck as CheckCircle, Download, Trash2, MessageSquare, X, CircleAlert as AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { exportEquipamentosToExcel } from '../utils/excelExports';

type Priority = 'low' | 'medium' | 'high';
type StatusFE = 'pending' | 'in_progress' | 'completed';

interface EquipmentRequest {
  id: string;
  equipment: string;
  justification: string;
  priority: Priority;
  status: StatusFE;
  requestDate: Date;
  user: string;
  userEmail: string;
  unreadMessages?: number;
}

interface Message {
  id: string;
  solicitacao_id: string;
  usuario_id: string;
  mensagem: string;
  tipo: 'usuario' | 'ti';
  created_at: string;
  usuario_nome?: string;
}

function formatDateBR(d: Date) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '—';
  try { return new Intl.DateTimeFormat('pt-BR').format(d); } catch { return '—'; }
}

function formatDateTime(dateStr: string) {
  try {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch {
    return '—';
  }
}

const mapStatus = (raw: any): StatusFE => {
  const v = String(raw ?? '').toLowerCase();
  if (v === 'in_progress' || v === 'em_andamento' || v === 'em andamento') return 'in_progress';
  if (v === 'completed' || v === 'concluido' || v === 'concluido') return 'completed';
  return 'pending';
};

const mapPriority = (raw: any): Priority => {
  const v = String(raw ?? '').toLowerCase();
  return (v === 'low' || v === 'high' || v === 'medium') ? (v as Priority) : 'medium';
};

const Equipamentos: React.FC = () => {
  const { user } = useAuth();
  const { addActivity } = useGamification();

  const [formData, setFormData] = useState({
    equipment: '',
    customEquipment: '',
    justification: '',
    priority: 'medium' as Priority,
  });
  const [submitting, setSubmitting] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [requests, setRequests] = useState<EquipmentRequest[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFE | 'all'>('all');

  const [selectedRequest, setSelectedRequest] = useState<EquipmentRequest | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const canViewAllRequests = useMemo(() => {
    const setor = user?.setor;
    const role = user?.role;
    return setor === 'RH' || setor === 'TI' || role === 'admin' || role === 'ti';
  }, [user]);

  const canExportReports = useMemo(() => {
    const setor = user?.setor;
    const role = user?.role;
    return setor === 'RH' || setor === 'TI' || role === 'admin' || role === 'ti';
  }, [user]);

  const canEditStatus = useMemo(() => {
    const setor = user?.setor;
    const role = user?.role;
    return setor === 'TI' || role === 'admin' || role === 'ti';
  }, [user]);

  const isTI = useMemo(() => {
    const setor = user?.setor;
    const role = user?.role;
    return setor === 'TI' || role === 'ti';
  }, [user]);

  const priorityColors: Record<Priority, string> = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-red-100 text-red-800',
  };

  const statusColors: Record<StatusFE, string> = {
    pending: 'bg-amber-100 text-amber-800 border border-amber-200',
    in_progress: 'bg-sky-100 text-sky-800 border border-sky-200',
    completed: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  };

  const priorityLabels: Record<Priority, string> = {
    low: 'Baixa',
    medium: 'Média',
    high: 'Alta',
  };

  const statusLabels: Record<StatusFE, string> = {
    pending: 'Pendente',
    in_progress: 'Em Andamento',
    completed: 'Concluído',
  };

  const equipmentTypes = [
    { id: 'notebook', name: 'Notebook', icon: Monitor },
    { id: 'desktop', name: 'Computador Desktop', icon: Monitor },
    { id: 'smartphone', name: 'Smartphone', icon: Smartphone },
    { id: 'mouse', name: 'Mouse', icon: Mouse },
    { id: 'keyboard', name: 'Teclado', icon: Keyboard },
    { id: 'headset', name: 'Headset', icon: Headphones },
    { id: 'printer', name: 'Impressora', icon: Printer },
    { id: 'other', name: 'Outros', icon: Monitor },
  ];

  useEffect(() => {
    if (!user) return;
    void loadRequests();
  }, [user, canViewAllRequests]);

  const loadRequests = async () => {
    if (!user) return;

    try {
      setListLoading(true);

      let query = supabase
        .from('ti_solicitacoes')
        .select('*')
        .order('created_at', { ascending: false });

      if (!canViewAllRequests) {
        query = query.eq('usuario_id', user.dbId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[Equipamentos] Error loading requests:', error);
        toast.error('Erro ao carregar solicitações');
        setRequests([]);
        return;
      }

      const transformed: EquipmentRequest[] = (data || []).map((req: any) => ({
        id: req.id,
        equipment: req.equipamento,
        justification: req.descricao,
        priority: mapPriority(req.prioridade),
        status: mapStatus(req.status),
        requestDate: new Date(req.created_at),
        user: req.solicitante_nome,
        userEmail: req.solicitante_email || '',
      }));

      setRequests(transformed);
    } catch (err) {
      console.error('Erro ao carregar solicitações:', err);
      toast.error('Erro ao carregar solicitações');
      setRequests([]);
    } finally {
      setListLoading(false);
    }
  };

  const loadMessages = async (requestId: string) => {
    try {
      setLoadingMessages(true);

      const { data, error } = await supabase
        .from('ti_mensagens')
        .select(`
          *,
          usuarios(nome)
        `)
        .eq('solicitacao_id', requestId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[HELPDESK] Error loading messages:', error);
        toast.error('Erro ao carregar mensagens');
        return;
      }

      const formattedMessages: Message[] = (data || []).map((msg: any) => ({
        id: msg.id,
        solicitacao_id: msg.solicitacao_id,
        usuario_id: msg.usuario_id,
        mensagem: msg.mensagem,
        tipo: msg.tipo,
        created_at: msg.created_at,
        usuario_nome: msg.usuarios?.nome || 'Usuário',
      }));

      setMessages(formattedMessages);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleOpenChat = (request: EquipmentRequest) => {
    setSelectedRequest(request);
    loadMessages(request.id);
  };

  const handleCloseChat = () => {
    setSelectedRequest(null);
    setMessages([]);
    setNewMessage('');
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedRequest || !user?.dbId) return;

    setSendingMessage(true);
    try {
      const tipo = isTI ? 'ti' : 'usuario';

      const { error } = await supabase
        .from('ti_mensagens')
        .insert({
          solicitacao_id: selectedRequest.id,
          usuario_id: user.dbId,
          mensagem: newMessage.trim(),
          tipo: tipo,
        });

      if (error) throw error;

      setNewMessage('');
      await loadMessages(selectedRequest.id);
      toast.success('Mensagem enviada!');
    } catch (error: any) {
      console.error('[HELPDESK] Error sending message:', error);
      toast.error('Erro ao enviar mensagem: ' + (error.message || 'Tente novamente'));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateStatus = async (requestId: string, newStatus: StatusFE) => {
    try {
      const { error } = await supabase
        .from('ti_solicitacoes')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) {
        console.error('[EQUIPAMENTOS] Error updating status:', error);
        toast.error('Erro ao atualizar status');
        return;
      }

      toast.success('Status atualizado com sucesso!');
      await loadRequests();

      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteRequest = async (requestId: string, equipmentName: string) => {
    const confirmation = confirm(`Tem certeza que deseja excluir a solicitação de "${equipmentName}"?\n\nEsta ação não pode ser desfeita.`);

    if (!confirmation) return;

    try {
      const { error } = await supabase
        .from('ti_solicitacoes')
        .delete()
        .eq('id', requestId);

      if (error) {
        console.error('[EQUIPAMENTOS] Error deleting request:', error);
        toast.error('Erro ao excluir solicitação');
        return;
      }

      toast.success('Solicitação excluída com sucesso!');

      if (selectedRequest?.id === requestId) {
        handleCloseChat();
      }

      await loadRequests();
    } catch (error) {
      console.error('Erro ao excluir solicitação:', error);
      toast.error('Erro ao excluir solicitação');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.email) {
      toast.error('Você precisa estar logado para solicitar.');
      return;
    }

    const equipmentName = formData.equipment === 'other'
      ? formData.customEquipment.trim()
      : formData.equipment.trim();

    if (!equipmentName || !formData.justification.trim()) {
      toast.error('Preencha todos os campos obrigatórios!');
      return;
    }

    if (!user.dbId) {
      console.error('[EQUIPAMENTOS] User dbId not found:', user);
      toast.error('Erro: usuário não identificado. Faça login novamente.');
      return;
    }

    setSubmitting(true);
    try {
      const insertData = {
        usuario_id: user.dbId,
        solicitante_nome: user.name,
        solicitante_email: user.email,
        equipamento: equipmentName,
        descricao: formData.justification,
        prioridade: formData.priority,
        status: 'pending',
      };

      const { data, error } = await supabase
        .from('ti_solicitacoes')
        .insert(insertData)
        .select();

      if (error) {
        console.error('[EQUIPAMENTOS] Error creating request:', error);
        toast.error(`Erro ao enviar solicitação: ${error.message}`);
        return;
      }

      addActivity('equipment_request', `Solicitou: ${equipmentName}`, {
        equipment: equipmentName,
        priority: formData.priority
      });

      toast.success('Solicitação enviada com sucesso! O setor de TI foi notificado. +4 pontos');
      setFormData({ equipment: '', customEquipment: '', justification: '', priority: 'medium' });
      await loadRequests();
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      toast.error('Erro ao enviar solicitação');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRequests = useMemo(() => {
    if (statusFilter === 'all') return requests;
    return requests.filter(req => req.status === statusFilter);
  }, [requests, statusFilter]);

  const TitleRight = () => {
    if (canViewAllRequests) return <div className="text-sm text-secondary">{requests.length} solicitações totais</div>;
    return null;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-headline-lg text-on-surface">Helpdesk TI - Manutenção de Equipamentos</h1>
          <div className="flex items-center gap-3">
            {canExportReports && (
              <button
                onClick={exportEquipamentosToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-bold"
              >
                <Download className="w-4 h-4" />
                Exportar Excel
              </button>
            )}
            <TitleRight />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-outline-variant dark:border-gray-700">
          <h2 className="text-lg font-bold text-on-surface mb-6">Nova Solicitação de Manutenção</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-on-surface mb-3">Tipo de Equipamento</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {equipmentTypes.map((equipment) => (
                  <button
                    key={equipment.id}
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, equipment: equipment.id }))}
                    className={`p-4 border rounded-lg text-center transition-colors ${
                      formData.equipment === equipment.id
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-outline-variant hover:border-primary/30'
                    }`}
                  >
                    <equipment.icon className="w-6 h-6 mx-auto mb-2" />
                    <span className="text-sm font-medium">{equipment.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {formData.equipment === 'other' && (
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">Especificar Equipamento</label>
                <input
                  type="text"
                  value={formData.customEquipment}
                  onChange={(e) => setFormData((p) => ({ ...p, customEquipment: e.target.value }))}
                  placeholder="Descreva o equipamento necessário"
                  className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-on-surface mb-2">Prioridade</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData((p) => ({ ...p, priority: e.target.value as Priority }))}
                className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="low">Baixa</option>
                <option value="medium">Média</option>
                <option value="high">Alta</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface mb-2">Descrição do Problema</label>
              <textarea
                value={formData.justification}
                onChange={(e) => setFormData((p) => ({ ...p, justification: e.target.value }))}
                placeholder="Descreva o problema com o equipamento..."
                className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                rows={4}
                required
              />
            </div>

            <button
              type="submit"
              disabled={
                submitting ||
                !formData.equipment ||
                !formData.justification.trim() ||
                (formData.equipment === 'other' && !formData.customEquipment.trim())
              }
              className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary-dark transition-colors font-bold disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <Send className="w-4 h-4" />
              <span>{submitting ? 'Enviando...' : 'Enviar Solicitação'}</span>
            </button>
          </form>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-outline-variant dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-on-surface">
              {canViewAllRequests ? 'Todas as Solicitações' : 'Minhas Solicitações'}
            </h2>
            {canViewAllRequests && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFE | 'all')}
                className="px-3 py-2 border border-outline-variant rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="all">Todos os Status</option>
                <option value="pending">Pendente</option>
                <option value="in_progress">Em Andamento</option>
                <option value="completed">Concluído</option>
              </select>
            )}
          </div>

          {listLoading ? (
            <div className="text-center py-12 text-secondary">Carregando...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8">
              <Monitor className="w-12 h-12 text-secondary/40 mx-auto mb-4" />
              <p className="text-secondary">
                {requests.length === 0 ? 'Nenhuma solicitação encontrada' : 'Nenhuma solicitação com este status'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((request) => (
                <div
                  key={request.id}
                  className="p-4 border border-outline-variant rounded-lg hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-on-surface">{request.equipment}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${priorityColors[request.priority]}`}>
                          {priorityLabels[request.priority]}
                        </span>
                        {canEditStatus ? (
                          <select
                            value={request.status}
                            onChange={(e) => handleUpdateStatus(request.id, e.target.value as StatusFE)}
                            className={`px-2 py-1 text-xs font-medium rounded-full border-0 cursor-pointer ${statusColors[request.status]}`}
                          >
                            <option value="pending">Pendente</option>
                            <option value="in_progress">Em Andamento</option>
                            <option value="completed">Concluído</option>
                          </select>
                        ) : (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[request.status]}`}>
                            {statusLabels[request.status]}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-secondary whitespace-pre-wrap mb-3">
                        {request.justification}
                      </p>

                      <div className="flex items-center space-x-4 text-xs text-secondary">
                        {canViewAllRequests && <span>Solicitante: {request.user}</span>}
                        {canViewAllRequests && <span>•</span>}
                        <span>Data: {formatDateBR(request.requestDate)}</span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button
                        onClick={() => handleOpenChat(request)}
                        className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors relative"
                        title="Abrir chat"
                      >
                        <MessageSquare className="w-5 h-5" />
                      </button>
                      {request.status === 'completed' && (
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      )}
                      {canEditStatus && (
                        <button
                          onClick={() => handleDeleteRequest(request.id, request.equipment)}
                          className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                          title="Excluir solicitação"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedRequest && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="p-4 border-b border-outline-variant flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-on-surface">{selectedRequest.equipment}</h3>
                  <p className="text-sm text-secondary">Solicitante: {selectedRequest.user}</p>
                </div>
                <button
                  onClick={handleCloseChat}
                  className="p-2 text-secondary hover:bg-surface-container rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-surface-container-low">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-on-surface">Descrição do Problema:</p>
                      <p className="text-sm text-on-surface mt-1 whitespace-pre-wrap">{selectedRequest.justification}</p>
                    </div>
                  </div>
                </div>

                {loadingMessages ? (
                  <div className="text-center py-8 text-secondary">Carregando mensagens...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-secondary">
                    <MessageSquare className="w-12 h-12 text-secondary/40 mx-auto mb-2" />
                    <p>Nenhuma mensagem ainda. Inicie a conversa!</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isFromTI = msg.tipo === 'ti';
                    const isOwnMessage = msg.usuario_id === user?.dbId;

                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${isFromTI ? 'bg-primary/10 border-primary/20' : 'bg-white dark:bg-gray-700 border-outline-variant dark:border-gray-600'} border rounded-lg p-3`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-on-surface">
                              {isFromTI ? '🔧 TI' : msg.usuario_nome}
                            </span>
                            <span className="text-xs text-secondary ml-2">{formatDateTime(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-on-surface whitespace-pre-wrap">{msg.mensagem}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="p-4 border-t border-outline-variant dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    disabled={sendingMessage}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center space-x-2 font-bold"
                  >
                    <Send className="w-4 h-4" />
                    <span>{sendingMessage ? 'Enviando...' : 'Enviar'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Equipamentos;
export { Equipamentos };
